import React, { useRef, useState } from "react";
import { Sparkles, Upload, FileText, X, Trash2, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

const TYPES = ["prodotto", "servizio", "prenotazione", "esperienza", "corso", "evento", "abbonamento"];
const STATUS = {
  new: { l: "Nuovo", c: "text-[#00E676] bg-[#00E676]/10 border-[#00E676]/30" },
  update: { l: "Aggiorna", c: "text-[#FF9F45] bg-[#FF9F45]/10 border-[#FF9F45]/30" },
  existing: { l: "Già presente", c: "text-zinc-400 bg-white/5 border-white/10" },
  duplicate: { l: "Duplicato", c: "text-red-400 bg-red-500/10 border-red-500/30" },
};

// Importatore catalogo universale con AI. Riutilizzabile: passare parseUrl e commitUrl.
export default function CatalogImporter({ parseUrl, commitUrl, onDone, onClose }) {
  const [tab, setTab] = useState("file");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // {items, summary, existing_count}
  const [fileName, setFileName] = useState("");
  const fileRef = useRef(null);

  const readFile = (file) => new Promise((res) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.readAsDataURL(file);
  });

  const analyze = async () => {
    setBusy(true);
    try {
      let body;
      if (tab === "text") {
        if (!text.trim()) { setBusy(false); return toast.error("Incolla il testo del menu/listino"); }
        body = { kind: "text", text };
      } else {
        const file = fileRef.current?.files?.[0];
        if (!file) { setBusy(false); return toast.error("Seleziona un file"); }
        const dataUrl = await readFile(file);
        body = { kind: "file", filename: file.name, content: dataUrl };
      }
      const { data } = await api.post(parseUrl, body);
      const items = (data.items || []).map((i) => ({ ...i, include: i.status !== "duplicate" && i.status !== "existing" }));
      setResult({ ...data, items });
      if (!items.length) toast("Nessun elemento riconosciuto. Riprova con un file più leggibile.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore durante l'analisi AI");
    } finally { setBusy(false); }
  };

  const upd = (idx, patch) => setResult((r) => ({ ...r, items: r.items.map((it, i) => i === idx ? { ...it, ...patch } : it) }));
  const del = (idx) => setResult((r) => ({ ...r, items: r.items.filter((_, i) => i !== idx) }));

  const commit = async () => {
    const chosen = result.items.filter((i) => i.include);
    if (!chosen.length) return toast.error("Seleziona almeno un elemento");
    setBusy(true);
    try {
      const payload = { items: chosen.map((i) => ({
        match_id: i.status === "update" ? i.match_id : null,
        type: i.type, name: i.name, description: i.description, price: Number(i.price) || 0,
        category: i.category, duration_min: i.duration_min, variants: i.variants || [], active: true,
      })) };
      const { data } = await api.post(commitUrl, payload);
      toast.success(`Importati: ${data.created} nuovi, ${data.updated} aggiornati`);
      onDone && onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nel salvataggio");
    } finally { setBusy(false); }
  };

  const s = result?.summary;
  const chosenCount = result ? result.items.filter((i) => i.include).length : 0;

  return (
    <div className="fixed inset-0 z-[60] bg-black/70 flex items-end sm:items-center justify-center p-2 sm:p-4" onClick={onClose} data-testid="catalog-importer">
      <div className="bg-[#131316] border border-white/12 rounded-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-[#131316] z-10 flex items-center justify-between px-4 py-3 border-b border-white/10">
          <div className="flex items-center gap-2 font-semibold"><Sparkles className="h-5 w-5 text-[#00E676]" /> Importa con AI</div>
          <button data-testid="importer-close" onClick={onClose}><X className="h-5 w-5 text-zinc-400" /></button>
        </div>

        {!result && (
          <div className="p-4 space-y-3">
            <p className="text-sm text-zinc-400">Carica una foto del menu, un PDF, un CSV/Excel o incolla il testo del listino. L'AI estrae categorie, prodotti/servizi, prezzi e varianti — senza inventare nulla.</p>
            <div className="flex gap-2">
              <button data-testid="importer-tab-file" onClick={() => setTab("file")} className={`flex-1 rounded-lg py-2 text-sm font-medium border ${tab === "file" ? "bg-[#00E676]/15 border-[#00E676]/40 text-[#00E676]" : "border-white/10 text-zinc-400"}`}><Upload className="h-4 w-4 inline mr-1" /> File</button>
              <button data-testid="importer-tab-text" onClick={() => setTab("text")} className={`flex-1 rounded-lg py-2 text-sm font-medium border ${tab === "text" ? "bg-[#00E676]/15 border-[#00E676]/40 text-[#00E676]" : "border-white/10 text-zinc-400"}`}><FileText className="h-4 w-4 inline mr-1" /> Testo</button>
            </div>
            {tab === "file" ? (
              <label className="block border-2 border-dashed border-white/15 rounded-xl p-6 text-center cursor-pointer hover:border-[#00E676]/40">
                <input ref={fileRef} type="file" accept=".png,.jpg,.jpeg,.webp,.pdf,.csv,.tsv,.xlsx,.xls,.txt" className="hidden" data-testid="importer-file" onChange={(e) => setFileName(e.target.files?.[0]?.name || "")} />
                <Upload className="h-6 w-6 mx-auto text-zinc-500 mb-2" />
                <div className="text-sm text-zinc-300">{fileName || "Foto menu · PDF · CSV/Excel · TXT"}</div>
              </label>
            ) : (
              <textarea data-testid="importer-text" value={text} onChange={(e) => setText(e.target.value)} rows={7} placeholder={"Es.\nPIZZE\nMargherita 6.50\nDiavola 8\n\nSERVIZI\nTaglio uomo 18 - 40 min"} className="w-full bg-[#0A0A0C] border border-white/10 rounded-lg p-3 text-sm" />
            )}
            <button data-testid="importer-analyze" disabled={busy} onClick={analyze} className="w-full bg-[#00E676] text-black font-semibold rounded-lg py-2.5 flex items-center justify-center gap-2 disabled:opacity-60">
              {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Analisi in corso…</> : <><Sparkles className="h-4 w-4" /> Analizza con AI</>}
            </button>
          </div>
        )}

        {result && (
          <div className="p-4 space-y-3">
            <div className="rounded-xl bg-[#00E676]/10 border border-[#00E676]/30 px-4 py-3 text-sm" data-testid="importer-summary">
              Trovati <b className="text-[#00E676]">{s.total}</b> elementi ({s.products} prodotti · {s.services} servizi) in <b className="text-[#00E676]">{s.categories}</b> categorie.
              {result.existing_count > 0 && <span className="text-zinc-400"> · {result.existing_count} già in catalogo (rilevati aggiornamenti/duplicati).</span>}
            </div>
            <div className="space-y-2 max-h-[46vh] overflow-y-auto pr-1" data-testid="importer-review">
              {result.items.map((it, i) => (
                <div key={it.tmp_id} data-testid={`importer-row-${i}`} className={`rounded-xl border p-2.5 ${it.include ? "border-white/10 bg-[#1C1C1F]" : "border-white/5 bg-[#141416] opacity-60"}`}>
                  <div className="flex items-center gap-2">
                    <input type="checkbox" checked={it.include} onChange={(e) => upd(i, { include: e.target.checked })} data-testid={`importer-include-${i}`} />
                    <input value={it.name} onChange={(e) => upd(i, { name: e.target.value })} className="flex-1 bg-[#0A0A0C] border border-white/10 rounded px-2 py-1 text-sm font-medium" />
                    <span className={`text-[10px] px-1.5 py-0.5 rounded border shrink-0 ${STATUS[it.status]?.c}`}>{STATUS[it.status]?.l || it.status}</span>
                    <button onClick={() => del(i)} className="text-zinc-500 hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
                  </div>
                  <div className="grid grid-cols-3 gap-1.5 mt-1.5">
                    <select value={it.type} onChange={(e) => upd(i, { type: e.target.value })} className="bg-[#0A0A0C] border border-white/10 rounded px-1.5 py-1 text-xs">
                      {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                    <input value={it.category} onChange={(e) => upd(i, { category: e.target.value })} placeholder="Categoria" className="bg-[#0A0A0C] border border-white/10 rounded px-2 py-1 text-xs" />
                    <input type="number" step="0.01" value={it.price} onChange={(e) => upd(i, { price: e.target.value })} placeholder="€" className="bg-[#0A0A0C] border border-white/10 rounded px-2 py-1 text-xs" />
                  </div>
                  {it.variants?.length > 0 && <div className="mt-1 text-[11px] text-zinc-500">{it.variants.map((v) => `${v.name} €${v.price}`).join(" · ")}</div>}
                  {it.duration_min ? <div className="mt-0.5 text-[11px] text-[#7DF9FF]">{it.duration_min} min</div> : null}
                </div>
              ))}
              {result.items.length === 0 && <div className="text-zinc-500 text-sm py-6 text-center">Nessun elemento. Torna indietro e riprova.</div>}
            </div>
            <div className="flex gap-2">
              <button data-testid="importer-back" onClick={() => setResult(null)} className="px-4 rounded-lg border border-white/15 text-sm">Indietro</button>
              <button data-testid="importer-commit" disabled={busy || !chosenCount} onClick={commit} className="flex-1 bg-[#00E676] text-black font-semibold rounded-lg py-2.5 flex items-center justify-center gap-2 disabled:opacity-60">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Conferma e salva ({chosenCount})
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
